import { RetentionMode } from "./notetaker-settings"

export const NOTETAKER_POLICY_BEHAVIORS = ["inherit", "prefer", "enforce"] as const
export type NotetakerPolicyBehavior = (typeof NOTETAKER_POLICY_BEHAVIORS)[number]

export const NOTETAKER_POLICY_BEHAVIOR_LABELS: Record<NotetakerPolicyBehavior, string> = {
  inherit: "Inherit",
  prefer: "Prefer",
  enforce: "Enforce",
}

export function toNotetakerPolicyBehavior(value: string): NotetakerPolicyBehavior | undefined {
  return NOTETAKER_POLICY_BEHAVIORS.find((behavior) => behavior === value)
}

export const TRANSCRIPT_VISIBILITIES = ["advisor", "hidden"] as const
export type TranscriptVisibility = (typeof TRANSCRIPT_VISIBILITIES)[number]

export const TRANSCRIPT_VISIBILITY_LABELS: Record<TranscriptVisibility, string> = {
  advisor: "Advisors can view",
  hidden: "Hidden from advisors",
}

export function toTranscriptVisibility(value: string): TranscriptVisibility | undefined {
  return TRANSCRIPT_VISIBILITIES.find((visibility) => visibility === value)
}

export const NOTETAKER_POLICY_CAPTURE_MODES = ["audio_video", "transcript_only"] as const
export type NotetakerPolicyCaptureMode = (typeof NOTETAKER_POLICY_CAPTURE_MODES)[number]

export const NOTETAKER_POLICY_CAPTURE_MODE_LABELS: Record<NotetakerPolicyCaptureMode, string> = {
  audio_video: "Audio and video",
  transcript_only: "Transcript only (no recording)",
}

export function toNotetakerPolicyCaptureMode(value: unknown): NotetakerPolicyCaptureMode | undefined {
  return NOTETAKER_POLICY_CAPTURE_MODES.find((mode) => mode === value)
}

export type NotetakerPolicyOwnerType = "BrokerDealer" | "Organization" | "Company" | "Book"

export const NOTETAKER_POLICY_OWNER_LABELS: Record<NotetakerPolicyOwnerType, string> = {
  BrokerDealer: "Broker dealer",
  Organization: "Organization",
  Company: "Company",
  Book: "Book",
}

export interface NotetakerPolicyOwner {
  type: NotetakerPolicyOwnerType
  id: string
  name: string
}

export type NotetakerPolicyRestriction = "enforced_by_ancestor" | "not_available_at_level"

export const CONSENT_TRACKING_MODES = ["off", "inferred", "confirmed", "inferred_and_confirmed"] as const
export type ConsentTrackingMode = (typeof CONSENT_TRACKING_MODES)[number]

export const CONSENT_TRACKING_LABELS: Record<ConsentTrackingMode, string> = {
  off: "Off",
  inferred: "Detect from transcript",
  confirmed: "Advisor confirms",
  inferred_and_confirmed: "Detect and advisor confirms",
}

export function toConsentTrackingMode(value: string): ConsentTrackingMode | undefined {
  return CONSENT_TRACKING_MODES.find((mode) => mode === value)
}

export interface NotetakerPolicyRetentionValue {
  mode: RetentionMode
  days: number | null
}

export interface NotetakerPolicyInheritanceLevel<Value> extends NotetakerPolicyOwner {
  behavior: NotetakerPolicyBehavior
  value: Value | null
}

export interface NotetakerPolicyOverriddenBelow {
  count: number
  names: string[]
}

interface NotetakerPolicySettingBase<Key extends string, Value> {
  key: Key
  system_default: Value
  local_behavior: NotetakerPolicyBehavior
  local_value: Value | null
  effective_value: Value
  source: NotetakerPolicyOwner | null
  locked: boolean
  can_edit: boolean
  restriction: NotetakerPolicyRestriction | null
  inheritance: NotetakerPolicyInheritanceLevel<Value>[]
  overridden_below: NotetakerPolicyOverriddenBelow
}

export type NotetakerPolicyBooleanSettingKey =
  | "automatic_attendance"
  | "skip_recurring_meetings"
  | "internal_meetings_private"
  | "announcement_required"
  | "transcript_email"
  | "ai_notice"
  | "outside_recordings"
  | "phone_recording"

export type NotetakerPolicyTextSettingKey = "announcement_text" | "ai_notice_text"

export type NotetakerPolicyBooleanSetting = NotetakerPolicySettingBase<NotetakerPolicyBooleanSettingKey, boolean>
export type NotetakerPolicyRetentionSetting = NotetakerPolicySettingBase<"retention", NotetakerPolicyRetentionValue>
export type NotetakerPolicyTextSetting = NotetakerPolicySettingBase<NotetakerPolicyTextSettingKey, string | null>
export type NotetakerPolicyTranscriptVisibilitySetting = NotetakerPolicySettingBase<
  "transcript_visibility",
  TranscriptVisibility
>
export type NotetakerPolicyCaptureModeSetting = NotetakerPolicySettingBase<"capture_mode", NotetakerPolicyCaptureMode>
export type NotetakerPolicyConsentTrackingSetting = NotetakerPolicySettingBase<
  "consent_tracking",
  ConsentTrackingMode
> & {
  options: ConsentTrackingMode[]
}

export type NotetakerPolicySetting =
  | NotetakerPolicyBooleanSetting
  | NotetakerPolicyRetentionSetting
  | NotetakerPolicyTextSetting
  | NotetakerPolicyTranscriptVisibilitySetting
  | NotetakerPolicyCaptureModeSetting
  | NotetakerPolicyConsentTrackingSetting

export type NotetakerPolicySettingKey = NotetakerPolicySetting["key"]
export type NotetakerPolicySettingValue = NotetakerPolicySetting["effective_value"]

export interface NotetakerPolicy {
  owner: NotetakerPolicyOwner
  levels_below_count: number
  announcement_audio_url: string | null
  ai_notice_default_text: string
  settings: NotetakerPolicySetting[]
}

export interface NotetakerPolicyResponse {
  notetaker_policy: NotetakerPolicy
}

export interface NotetakerPolicyUpdatePayload {
  automatic_attendance_behavior?: NotetakerPolicyBehavior
  automatic_attendance?: boolean
  skip_recurring_meetings_behavior?: NotetakerPolicyBehavior
  skip_recurring_meetings?: boolean
  internal_meetings_private_behavior?: NotetakerPolicyBehavior
  internal_meetings_private?: boolean
  capture_mode_behavior?: NotetakerPolicyBehavior
  capture_mode?: NotetakerPolicyCaptureMode
  announcement_required_behavior?: NotetakerPolicyBehavior
  announcement_required?: boolean
  announcement_text_behavior?: NotetakerPolicyBehavior
  announcement_text?: string | null
  transcript_email_behavior?: NotetakerPolicyBehavior
  transcript_email?: boolean
  transcript_visibility_behavior?: NotetakerPolicyBehavior
  transcript_visibility?: TranscriptVisibility
  outside_recordings_behavior?: NotetakerPolicyBehavior
  outside_recordings?: boolean
  phone_recording_behavior?: NotetakerPolicyBehavior
  phone_recording?: boolean
  retention_behavior?: NotetakerPolicyBehavior
  retention_mode?: RetentionMode
  retention_days?: number | null
  ai_notice_behavior?: NotetakerPolicyBehavior
  ai_notice?: boolean
  ai_notice_text_behavior?: NotetakerPolicyBehavior
  ai_notice_text?: string | null
  consent_tracking_behavior?: NotetakerPolicyBehavior
  consent_tracking?: ConsentTrackingMode
}
